import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import { BehaviorSubject, combineLatest, from, Observable, of, throwError } from "rxjs";
import { catchError, concatMap, shareReplay, take, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";

@Injectable({ providedIn: "root" })
export class AuthService {
  public isAuthenticated$: Observable<any>;
  public loggedIn: boolean = null;
  public navOpen: boolean = true;
  public token$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public userProfile$: Observable<any>;

  private auth0Client$: Observable<Auth0Client>
  private claims$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private handleRedirectCallback$: Observable<any>;
  private getIdClaims$: Observable<any>;
  private getTokenSilently$: Observable<any>;
  private userProfileSubject$: BehaviorSubject<any>;

  public constructor(private router: Router) {
    this.auth0Client$ = (from(
      createAuth0Client({
        authorizationParams: {
          audience: environment.auth.AUDIENCE,
          redirect_uri: environment.auth.REDIRECT,
          scope: "openid profile email"
        },
        clientId: environment.auth.CLIENT_ID,
        domain: environment.auth.CLIENT_DOMAIN
      })
    ) as Observable<Auth0Client>).pipe(
      shareReplay(1), // Every subscription receives the same shared value
      catchError((err: unknown) => throwError((): unknown => err))
    );

    this.token$ = new BehaviorSubject<string>(null);
    this.claims$ = new BehaviorSubject<string>(null);

    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.isAuthenticated()))
    );

    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
    );

    this.userProfileSubject$ = new BehaviorSubject<any>(null);
    this.userProfile$ = this.userProfileSubject$.asObservable();

    this.getTokenSilently$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getTokenSilently()))
    );

    this.getIdClaims$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getIdTokenClaims()))
    );
  }

  getUser$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser()))
    );
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user data
          return combineLatest([
            this.getUser$(),
            this.getTokenSilently$,
            this.getIdClaims$
          ]);
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    const checkAuthSub = checkAuth$.subscribe(
      (response: { [key: string]: any } | boolean) => {
        // If authenticated, response will be user object
        // If not authenticated, response will be 'false'
        // Set subjects appropriately
        if (response) {
          // @ts-ignore
          const user: any = response[0];
          // @ts-ignore
          const token: any = response[1];
          // @ts-ignore
          const claims: any = response[2];
          // Add Current Tenant ID
          user.tenants = this.getTenantsFromUser(user);
          
          // Validate that user has at least one tenant
          if (!user.tenants || user.tenants.length === 0) {
            console.error('[AuthService] User has no tenants assigned in localAuthSetup');
            this.loggedIn = false;
            checkAuthSub.unsubscribe();
            return;
          }
          
          let tenantForce = new URL(location.href).searchParams.get("tenant");
          if (tenantForce) {
            user.currentTenant = user.tenants.find(
              (t: any): boolean => t.tenant_id === tenantForce
            );
          } else if (localStorage.getItem("currentTenantId")) {
            user.currentTenant = user.tenants.find(
              (t: any): boolean => t.tenant_id === localStorage.getItem("currentTenantId")
            );
          } 
          
          // Fallback to first tenant if no match found
          if (!user.currentTenant) {
            user.currentTenant = user.tenants[0];
          }

          this.userProfileSubject$.next(user);
          this.token$.next(token);
          this.claims$.next(claims);
          this.loggedIn = true;
        } else {
          this.loggedIn = false;
        }
        // Clean up subscription
        checkAuthSub.unsubscribe();
      }
    );
  }

  changeTenant(tenant: any): Observable<any> {
    return this.userProfileSubject$.pipe(
      tap((res) => {
        res.tenants.forEach((t: any): void => {
          if (t.tenant_id === tenant.tenant_id) {
            res.currentTenant = t;
            localStorage.setItem("currentTenantId", t.tenant_id);
            location.replace(location.toString().split("/")[0]); // back to dashboard
          }
        });
      })
    );
  }

  getTenantsFromUser(user: any) {
    const tenantMaps = [];
    for (const keyl in user) {
      if (
        keyl.includes(
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/roles/"
        )
      ) {
        const tenant_id = keyl.replace(
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/roles/",
          ""
        );
        tenantMaps.push({
          tenant_id: tenant_id,
          roles: user[keyl],
          name: user["http://tenant.name/" + tenant_id],
        });
      }
    }
    return tenantMaps;
  }

  /**
   * Login with Auth0
   */
  public login() {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.pipe(
      take(1) // Only take the first emission and auto-unsubscribe
    ).subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect();
    });
  }

  getLoggedInUserEmail() {
    let email: any;
    this.userProfileSubject$.pipe(
      tap((res) => {
        email = res.email;
      })
    );
    return email;
  }

  /*
   * Only the callback component should call this method. Call when app reloads after user logs in with Auth0.
   */
  handleAuthCallback() {
    let targetRoute: string; // Path to redirect to after login processed
    // Ensure Auth0 client instance exists
    this.auth0Client$.pipe(
      // Have client, now call method to handle auth callback redirect
      concatMap(() => this.handleRedirectCallback$),
      tap((cbRes) => {
        // Get and set target redirect route from callback results
        targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
      }),
      concatMap(() => {
        // Redirect callback complete; create stream returning user data and authentication status
        return combineLatest([
          this.getUser$(),
          this.getTokenSilently$,
          this.getIdClaims$,
          this.isAuthenticated$
        ]);
      }),
      catchError((err: unknown) => {
        console.error('[AuthService] Error handling auth callback:', err);
        // Log detailed error for SSO troubleshooting
        if (err instanceof Error) {
          console.error('[AuthService] Error name:', err.name, 'Message:', err.message);
        }
        try {
          console.error('[AuthService] Error details:', JSON.stringify(err, null, 2));
        } catch (e) {
          console.error('[AuthService] Could not stringify error object');
        }
        // Redirect to home on error
        this.router.navigate(['/']);
        return throwError(() => err);
      })
    )
    .subscribe({
      next: ([user, token, claims, loggedIn]) => { // Response will be an array of user and login status
        // Update subjects and loggedIn property
        const claims2: any = claims;
        // Add Current Tenant ID
        user.tenants = this.getTenantsFromUser(user);
        
        // Validate that user has at least one tenant
        if (!user.tenants || user.tenants.length === 0) {
          console.error('[AuthService] User has no tenants assigned');
          this.router.navigate(['/']);
          return;
        }
        
        user.currentTenant = user.tenants[0];
        localStorage.setItem("currentTenantId", user.currentTenant.tenant_id);
        this.userProfileSubject$.next(user);
        this.token$.next(token);
        this.claims$.next(claims2);
        this.loggedIn = loggedIn;
        // Redirect to target route after callback processing
        this.router.navigate([targetRoute]);
      },
      error: (err) => {
        console.error('[AuthService] Subscription error in handleAuthCallback:', err);
        this.router.navigate(['/']);
      }
    });
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.pipe(
      take(1) // Only take the first emission and auto-unsubscribe
    ).subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        clientId: environment.auth.CLIENT_ID,
        logoutParams: {
          returnTo: environment.auth.LOGOUT_URL
        }
      });
    });
  }
}
